/*
  Sprint 7A -- Mobile Sticky Add to Cart.

  Observes the existing <product-form data-section-id="..."> wrapper
  (rendered by snippets/buy-buttons.liquid, unmodified) rather than only the
  submit button inside it: that wrapper's box includes the submit button and,
  when a merchant enables dynamic checkout, the Shop Pay/payment button
  rendered alongside it. Observing just the submit button could reveal this
  bar while a payment button is still on-screen below it; observing the form
  wrapper means the bar only appears once the whole purchase-action area has
  left the viewport. No markup change was needed for this -- <product-form>
  already exists as the natural minimal wrapper.

  Synchronization reuses existing infrastructure only:
  - PUB_SUB_EVENTS.variantChange (constants.js/pubsub.js), already published
    by product-info.js on every variant fetch, already carrying the fetched
    HTML fragment -- reused the same way product-info.js updates its own
    price element, so no formatting/currency logic is duplicated here.
  - A MutationObserver mirroring the existing submit button's
    disabled/aria-disabled/class state, covering both variant-driven
    disabling and product-form.js's in-flight submit state.
  - form="..." for submission itself -- no cart/fetch logic in this file.
*/
if (!customElements.get('sticky-cart-bar')) {
  customElements.define(
    'sticky-cart-bar',
    class StickyCartBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.priceEl = this.querySelector('.sticky-cart__price');
        this.button = this.querySelector('.sticky-cart__button');
        this.sourceButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.target = document.querySelector(`product-form[data-section-id="${this.sectionId}"]`);

        if (!this.target || !this.sourceButton) return; // no purchase form in this configuration -- stay off

        this.mql = window.matchMedia('(min-width: 750px)');
        this.handleMqlChange = this.syncObserver.bind(this);
        this.mql.addEventListener('change', this.handleMqlChange);
        this.syncObserver();

        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.updateFromVariantChange(event.data);
        });

        this.buttonObserver = new MutationObserver(() => this.mirrorButtonState());
        this.buttonObserver.observe(this.sourceButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });
        this.mirrorButtonState();
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.variantUnsubscribe?.();
        this.mql?.removeEventListener('change', this.handleMqlChange);
      }

      syncObserver() {
        if (this.mql.matches) {
          this.intersectionObserver?.disconnect();
          this.intersectionObserver = null;
          this.setAttribute('hidden', '');
          return;
        }
        if (this.intersectionObserver) return;
        this.intersectionObserver = new IntersectionObserver(
          (entries) => this.toggleAttribute('hidden', entries[0].isIntersecting),
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.target);
      }

      updateFromVariantChange({ html }) {
        const sourcePrice = html.getElementById(`price-${this.sectionId}`);
        if (sourcePrice && this.priceEl) this.priceEl.innerHTML = sourcePrice.innerHTML;
      }

      mirrorButtonState() {
        if (!this.button) return;
        this.button.disabled = this.sourceButton.hasAttribute('disabled');
        this.button.toggleAttribute('aria-disabled', this.sourceButton.getAttribute('aria-disabled') === 'true');
        this.button.classList.toggle('loading', this.sourceButton.classList.contains('loading'));
      }
    }
  );
}
